import { Divider, makeStyles } from '@material-ui/core';
import React from 'react';
import { usePrefixTranslation } from '../../../i18n';
import { actions, useTypedDispatch, useTypedSelector } from '../../../redux';
import { theme } from '../../../theme/theme';
import { Project } from '../../../types/project';
import { User } from '../../../types/user';
import { Avatar, Button, Checkbox, Chip, ProgressBar } from '../../global';

const useInProgressClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        '& > hr': {
            background: theme.colors.neutral[3],
            marginBottom: 20,
        },
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        flex: '1 0 auto',
        height: 0,
        overflowY: 'auto',
        gap: 20,
    },
    footer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    labels: {
        display: 'flex',
        flexDirection: 'column',
        flex: '0 0 200px',
        color: theme.colors.darkBlue[5],
        '& > span:first-of-type': {
            ...theme.typography.content.label.strong,
        },
        '& > span:last-of-type': {
            ...theme.typography.content.caption.paragraph,
        },
    },
});

/**
 * Editor for projects in the in-progress mode.
 */
export const InProgress = () => {
    const classes = useInProgressClasses();
    const { t } = usePrefixTranslation('project', { project: 'editor.InProgress' });
    const dispatch = useTypedDispatch();
    const { project, users } = useTypedSelector(state => ({ project: state.project.changed, users: state.user.users }));

    React.useEffect(() => void dispatch(actions.user.get()), []);

    if (!project) return <span>{'#project not in store'}</span>;

    const allPreparationTasks = Object.values(project.preparation_steps).flat();
    const allMethodsTasks = Object.values(project.methods)
        .map(tasks => Object.values(tasks ?? {}))
        .flat(3);
    const tasks = [...allPreparationTasks, ...allMethodsTasks];
    const done = tasks.reduce((acc, t) => acc + +!!t?.startsWith('\u200b'), 0);
    const completed = (done / Math.max(tasks.length, 1)) * 100;

    return (
        <div className={classes.root}>
            <div className={classes.content}>
                {(['internal', 'external'] as const).map(type => (
                    <React.Fragment key={type}>
                        {type in project.preparation_steps && (
                            <MethodTasks
                                type={type}
                                name={t('preparation')}
                                tasks={project.preparation_steps[type] ?? []}
                                user={users.find(u => u.id === project.user)}
                                onTaskCheck={(index, checked) => {
                                    const preparation: Project['preparation_steps'] = JSON.parse(
                                        JSON.stringify(project.preparation_steps),
                                    );
                                    const taskChecked = preparation[type]![index].startsWith('\u200b');
                                    if (checked === taskChecked) return;
                                    const tasks = preparation[type]!;
                                    tasks[index] = checked ? `\u200b${tasks[index]}` : tasks[index].slice(1);
                                    dispatch(actions.project.edit({ preparation_steps: preparation }));
                                    dispatch(actions.project.commit());
                                    dispatch(actions.project.update());
                                }}
                            />
                        )}
                        {type in project.methods &&
                            Object.entries(project.methods[type] ?? {}).map(([name, tasks]) => (
                                <MethodTasks
                                    key={name}
                                    type={type}
                                    name={name}
                                    tasks={tasks}
                                    user={users.find(u => u.id === project.user)}
                                    onTaskCheck={(index, checked) => {
                                        const methods: Project['methods'] = JSON.parse(JSON.stringify(project.methods));
                                        const taskChecked = methods[type]![name][index].startsWith('\u200b');
                                        if (checked === taskChecked) return;
                                        const tasks = methods![type]![name];
                                        tasks[index] = checked ? `\u200b${tasks[index]}` : tasks[index].slice(1);
                                        dispatch(actions.project.edit({ methods }));
                                        dispatch(actions.project.commit());
                                        dispatch(actions.project.update());
                                    }}
                                />
                            ))}
                    </React.Fragment>
                ))}
            </div>
            <Divider />
            <div className={classes.footer}>
                <div className={classes.labels}>
                    <span>{t('complete', { completed })}</span>
                    <span>{t('tasks', { done, tasks: tasks.length })}</span>
                    <ProgressBar variant='determinate' value={completed} />
                </div>
                <Button
                    variant='contained'
                    disabled={done < tasks.length}
                    onClick={() => {
                        dispatch(actions.project.edit({ status: 'awaiting report' }));
                        dispatch(actions.project.commit());
                        dispatch(actions.project.update(true)).catch(error => {
                            dispatch(actions.project.edit({ status: 'in progress' }));
                            dispatch(actions.project.commit());
                        });
                    }}
                >
                    {t('button-complete')}
                </Button>
            </div>
        </div>
    );
};

const useMethodTasksClasses = makeStyles({
    root: {
        display: 'flex',
        flexDirection: 'column',
        '& > hr': {
            color: theme.colors.neutral[3],
        },
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    labels: {
        display: 'flex',
        flexDirection: 'column',
        color: theme.colors.darkBlue[5],
        textTransform: 'capitalize',
        '& > span:first-child': {
            ...theme.typography.content.caption.paragraph,
        },
        '& > span:last-child': {
            ...theme.typography.headings.h5,
            marginBottom: 5,
        },
    },
    task: {
        display: 'flex',
        alignItems: 'center',
        padding: '10px 0',
        borderTop: `2px solid ${theme.colors.neutral[9]}`,
        '& > span:first-child': {
            margin: '-10px 0',
        },
        '& > span:last-child': {
            color: theme.colors.neutral[1],
            ...theme.typography.content.label.default,
        },
    },
});

/**
 * Component to list the tasks of a project method or preparation.
 * The task status is defined by its first character, if it is `'u\200b'` it is checked, otherwise it is not checked.
 * // TODO find better way to do this
 *
 * @param props.type type of the method or preparation
 * @param props.method method name
 * @param props.tasks of the method or preparation
 * @param props.user user assigned to the tasks
 * @param props.onTaskCheck callback triggered when the user clicks the checkbox in one of the tasks
 */
const MethodTasks = (props: {
    type: 'internal' | 'external';
    name: string;
    tasks: string[];
    user?: User;
    onTaskCheck?: (index: number, checked: boolean) => void;
}) => {
    const classes = useMethodTasksClasses();
    const { t } = usePrefixTranslation('common');

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <div className={classes.labels}>
                    <span>{t(`type.${props.type}`)}</span>
                    <span>{props.name}</span>
                </div>
                {props.user && (
                    <Chip
                        label={props.user.full_name}
                        avatar={<Avatar name={props.user.full_name} src={props.user.picture} />}
                    />
                )}
            </div>
            <Divider />
            {props.tasks.map((task, i) => (
                <div key={i} className={classes.task}>
                    <Checkbox
                        checked={task.startsWith('\u200b')}
                        onChange={event => props.onTaskCheck?.(i, event.target.checked)}
                    />
                    <span>{task}</span>
                </div>
            ))}
        </div>
    );
};
